import { Chip, Stack, Skeleton, Tooltip } from "@mui/material";
import { useMemo, useState } from "react";

import { AssignDragonTileModal } from "./AssignDragonTileModal";
import type { DragonTileChipsProps } from "./types";

import { useDragonTilesLite } from "@/api/dragon-tiles";
import { useDragonTileLabel } from "@/features/Connectors/byocTileDisplayName";

/**
 * Displays chips for dragon tiles that contain this tile
 */
export function DragonTileChips({
  innerTileId,
  siteId,
  assignFrameId,
}: DragonTileChipsProps) {
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const { data: dragonTiles, isLoading } = useDragonTilesLite(siteId, "all");
  const getLabel = useDragonTileLabel(siteId);

  const dragonTileNames = useMemo(() => {
    if (!dragonTiles || !innerTileId) return [];

    return dragonTiles
      .filter((dragonTile) =>
        dragonTile.inner_tiles?.some((tile) => tile.id === innerTileId),
      )
      .map(getLabel);
  }, [dragonTiles, innerTileId, getLabel]);

  if (!innerTileId && !assignFrameId) return null;

  if (isLoading && innerTileId) {
    return (
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Skeleton
          variant="rectangular"
          width={80}
          height={30}
          sx={{ borderRadius: 1 }}
        />
      </Stack>
    );
  }

  if (dragonTileNames.length === 0) {
    if (!assignFrameId) return null;
    return (
      <>
        <Tooltip title="Assign to dragon tile">
          <Chip
            label="Unassigned"
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setIsAssignModalOpen(true);
            }}
          />
        </Tooltip>
        <AssignDragonTileModal
          open={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          frameId={assignFrameId}
          siteId={siteId}
        />
      </>
    );
  }

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      {dragonTileNames.map((name) => (
        <Chip key={name} label={name} variant="lightGray" size="small" />
      ))}
    </Stack>
  );
}
